import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, Info, X, XCircle } from 'lucide-react';
import { Button, cn, IconButton } from './core';
import { TextArea } from './form';

// ---------------------------------------------------------------------------
// Dialog (native <dialog>: focus trap, Esc to close, inert background)
// ---------------------------------------------------------------------------

export function Dialog({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={cn(
        'm-auto w-[calc(100%-2rem)] rounded-2xl border border-border bg-surface p-0 shadow-2xl',
        wide ? 'max-w-3xl' : 'max-w-lg',
        'max-h-[calc(100dvh-2rem)]',
      )}
    >
      {open && (
        <div className="flex max-h-[calc(100dvh-2rem)] flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
            <h2 id={titleId} className="text-lg font-semibold">
              {title}
            </h2>
            <IconButton label="Close" onClick={onClose}>
              <X className="size-5" aria-hidden />
            </IconButton>
          </div>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

// ---------------------------------------------------------------------------
// Confirm / prompt (promise-based)
// ---------------------------------------------------------------------------

interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  /** Ask for a reason; the promise resolves to the text (or null if cancelled). */
  reason?: { label: string; minLength?: number; placeholder?: string };
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<string | boolean | null>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: string | boolean | null) => void }) | null>(null);
  const [text, setText] = useState('');
  const confirm = useCallback<ConfirmFn>((opts) => new Promise((resolve) => {
    setText('');
    setState({ ...opts, resolve });
  }), []);
  const close = (v: string | boolean | null) => {
    state?.resolve(v);
    setState(null);
  };
  const min = state?.reason?.minLength ?? 3;
  const reasonId = useId();
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={!!state}
        onClose={() => close(state?.reason ? null : false)}
        title={state?.title ?? ''}
        footer={
          <>
            <Button onClick={() => close(state?.reason ? null : false)}>Cancel</Button>
            <Button
              variant={state?.danger ? 'danger' : 'primary'}
              disabled={!!state?.reason && text.trim().length < min}
              onClick={() => close(state?.reason ? text.trim() : true)}
            >
              {state?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        {state?.body && <div className="mb-3 text-sm leading-relaxed">{state.body}</div>}
        {state?.reason && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor={reasonId} className="text-sm font-semibold">
              {state.reason.label}
            </label>
            <TextArea id={reasonId} value={text} onChange={(e) => setText(e.target.value)} placeholder={state.reason.placeholder} autoFocus />
            <p className="text-xs text-muted">{min > 0 ? `At least ${min} characters. ` : ''}This is kept in the history of changes.</p>
          </div>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const c = useContext(ConfirmContext);
  if (!c) throw new Error('useConfirm outside ConfirmProvider');
  return c;
}

// ---------------------------------------------------------------------------
// Toasts (aria-live)
// ---------------------------------------------------------------------------

type ToastTone = 'ok' | 'info' | 'warn' | 'danger';
interface Toast {
  id: number;
  tone: ToastTone;
  message: ReactNode;
  action?: { label: string; onClick: () => void };
}

interface ToastApi {
  show: (message: ReactNode, tone?: ToastTone, action?: Toast['action']) => void;
  error: (err: unknown) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong.';
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const dismiss = (id: number) => setToasts((t) => t.filter((x) => x.id !== id));
  const show = useCallback<ToastApi['show']>((message, tone = 'ok', action) => {
    const id = ++seq.current;
    setToasts((t) => [...t.slice(-3), { id, tone, message, action }]);
    window.setTimeout(() => dismiss(id), tone === 'danger' ? 9000 : action ? 7000 : 3500);
  }, []);
  const api: ToastApi = { show, error: (err) => show(errorMessage(err), 'danger') };
  const icon = { ok: CheckCircle2, info: Info, warn: AlertTriangle, danger: XCircle };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div aria-live="polite" aria-atomic="false" className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 lg:bottom-6 no-print">
        {toasts.map((t) => {
          const Icon = icon[t.tone];
          return (
            <div
              key={t.id}
              role={t.tone === 'danger' ? 'alert' : 'status'}
              className={cn(
                'pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border px-4 py-3 shadow-lg',
                t.tone === 'ok' && 'border-ok/50 bg-ok-soft text-ok',
                t.tone === 'info' && 'border-info/50 bg-info-soft text-info',
                t.tone === 'warn' && 'border-warn/50 bg-warn-soft text-warn',
                t.tone === 'danger' && 'border-danger/50 bg-danger-soft text-danger',
              )}
            >
              <Icon className="size-5 shrink-0" aria-hidden />
              <div className="flex-1 text-sm font-medium text-text">{t.message}</div>
              {t.action && (
                <button type="button" className="min-h-9 rounded-lg px-3 text-sm font-bold underline" onClick={() => { t.action!.onClick(); dismiss(t.id); }}>
                  {t.action.label}
                </button>
              )}
              <button type="button" aria-label="Dismiss" className="rounded p-1 text-text/70" onClick={() => dismiss(t.id)}>
                <X className="size-4" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const c = useContext(ToastContext);
  if (!c) throw new Error('useToast outside ToastProvider');
  return c;
}
